import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Order } from '@alpha-edge/shared'
import { BillingStatusBadge } from '../../components/ui/BillingStatusBadge'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import {
  PageHeader,
  DataTable,
  Pagination,
  StatusBadge,
  ColumnSettingsButton,
  btnPrimary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useTableColumns } from '../../hooks/use-table-columns'

// PLAN.md §7.6.24 Slice 4 -- server-side extra-observation count chip,
// embedded on the always-visible "Order #" column (mirrors ResultListPage's
// CRIT/ON HOLD badges on its "Barcode" column) rather than a separate
// hideable column, so the signal isn't opt-in.
function ExtraObservationChip({ count }: { readonly count: number }) {
  if (count <= 0) return null
  return (
    <span
      title={`${count} instrument observation${count === 1 ? '' : 's'} not in the ordered panel`}
      style={{
        display: 'inline-block',
        padding: '0.1rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        background: '#f1f5f9',
        color: '#64748b',
      }}
    >
      +{count}
    </span>
  )
}

const columns: readonly Column<Order>[] = [
  {
    key: 'orderNumber',
    header: 'Order #',
    sortable: true,
    filterable: true,
    render: (o) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <strong>{o.orderNumber || o.id.slice(0, 8)}</strong>
        <ExtraObservationChip count={o.extraObservationCount ?? 0} />
      </span>
    ),
  },
  { key: 'tests', header: 'Tests', filterable: true, render: (o) => o.testTypes.join(', ') },
  { key: 'priority', header: 'Priority', sortable: true, render: (o) => <StatusBadge status={o.priority} /> },
  { key: 'status', header: 'Status', sortable: true, render: (o) => <StatusBadge status={o.status} /> },
  {
    key: 'billingStatus',
    header: 'Billing',
    sortable: true,
    defaultVisible: false,
    render: (o) =>
      o.billingStatus && o.billingStatus !== 'none' ? <BillingStatusBadge status={o.billingStatus} /> : '--',
  },
  {
    key: 'provider',
    header: 'Provider',
    sortable: true,
    filterable: true,
    render: (o) => o.orderingProviderName || '--',
  },
  {
    key: 'patientLastName',
    header: 'Patient Last',
    sortable: true,
    filterable: true,
    defaultVisible: false,
    render: (o) => o.patientLastName || '--',
  },
  {
    key: 'patientFirstName',
    header: 'Patient First',
    sortable: true,
    filterable: true,
    defaultVisible: false,
    render: (o) => o.patientFirstName || '--',
  },
  {
    key: 'date',
    header: 'Created',
    sortable: true,
    render: (o) => new Date(o.createdAt).toLocaleDateString(),
    sortValue: (o) => new Date(o.createdAt).getTime(),
  },
]

const STATUS_OPTIONS = ['', 'draft', 'submitted', 'received', 'in_progress', 'completed', 'canceled'] as const

export function OrderListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage } = useApiList<Order>({
    path: '/orders',
    filters: { search: debouncedSearch || undefined, status: statusFilter || undefined },
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('orders')

  return (
    <div>
      <PageHeader
        title="Orders"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button onClick={() => navigate('/orders/new')} style={btnPrimary}>
              New Order
            </button>
          </>
        }
      />
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '250px' }}
          placeholder="Search by order #..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select style={input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.filter(Boolean).map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(o) => navigate(`/orders/${o.id}`)}
            emptyMessage="No orders found."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}
    </div>
  )
}
